'use client';

import { useState } from 'react';
import { usePathname } from 'next/navigation';

import { getPersistedReferralCode } from '@/hooks';
import { useGuestGate } from '@/hooks/useGuestGate';
import { OnboardingScreen } from './OnboardingScreen';

// Per-session flag: once a guest taps "Skip for now" we stop showing the splash
// for the rest of the session, but a fresh visit gets another chance to onboard.
const DISMISS_KEY = 'porvenix_onboarding_dismissed';

/**
 * Welcome/onboarding splash for signed-out users.
 *
 * Shown only to a definitive guest on the landing route who hasn't skipped this
 * session. The stepper ends in a playful trade demo whose confetti finale (and
 * "Skip for now") both dismiss the splash, revealing the trending/home page — where
 * interactive clicks still prompt login via GuestClickGuard.
 */
export const OnboardingGate = () => {
  const pathname = usePathname();
  const { isGuest } = useGuestGate();

  // Read the per-session flag lazily. On the server there's no storage (and
  // `isGuest` is false during hydration anyway, so this renders null either way —
  // no hydration mismatch).
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Invite arrivals carry a persisted referral code — for them we skip the splash so
  // the login modal (auto-opened by ReferralTracker) pops up on a clean home page.
  const hasReferralCode = typeof window !== 'undefined' && !!getPersistedReferralCode();

  // Only the landing page is taken over; other public pages (terms/privacy/contact,
  // often opened mid-signup) stay readable.
  const shouldShow = isGuest && pathname === '/' && !dismissed && !hasReferralCode;

  if (!shouldShow) return null;

  const handleSkip = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* storage unavailable — dismiss for this render only */
    }
    setDismissed(true);
  };

  // Both the demo finale and "Skip for now" dismiss the splash to reveal the page.
  return <OnboardingScreen onComplete={handleSkip} onSkip={handleSkip} />;
};

export default OnboardingGate;
